import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { FeatureManagementContext, useExperiment } from '@printdeal/feature-management/react';
import { usePreviews } from '@printdeal/previews';
import { useSelectorMachineContext } from '@printdeal/product-selector';
import { ProductGallery } from './ProductGallery';

import {
  getEnlargedImages, getGalleryImages, getGalleryInteractiveImage, getPreviews, getThumbnailImages,
  getVideoElement, getVideoImageElement, getVideoThumbnailElement, isImage, isInteractiveImage,
  isVideo, getUpdatedGalleryAssets, getCanvasPreviews,
} from './helpers/ProductGalleryHelper';
import {
  IProductGallery, ProductGalleryAsset, IProductGalleryImage, ImageComponent,
} from '../../../types/product/productDetailPage';
import { dispatchDataLayerEntry } from '../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { useScenePreviews } from '../../../hooks/useScenePreviews';
import { InteractiveImage } from '../../interactive-banner/InteractiveBanner';
import { loadYouTubeAPI } from '../../../helpers/youtubeHelper';
import { Preview } from '../../../types/design';

interface Props {
  title: string;
  gallery: IProductGallery;
  productKey?: string;
  documentReferenceUrl?: string | null;
  scenes?: Preview[];
  previewImage?: string | null;
}

const getEnlargedImageElement = (enlargedImage: IProductGalleryImage | undefined) => (
  enlargedImage ? getEnlargedImages([enlargedImage]) : []
);
const getThumbnailElement = (thumbnailImage: IProductGalleryImage | undefined) => (
  thumbnailImage ? getThumbnailImages([thumbnailImage]) : []
);
const getImageElement = (image: IProductGalleryImage | undefined) => (
  image ? getGalleryImages([image]) : []
);
const getInteractiveImageElement = (
  image: InteractiveImage | undefined,
) => (
  image ? getGalleryInteractiveImage([image]) : []
);

const handleThumbnailImage = (asset: ProductGalleryAsset, index: number) => {
  if (isInteractiveImage(asset)) {
    return getInteractiveImageElement(asset.thumbnailImage);
  }
  if (isVideo(asset) && asset.video) {
    return getVideoThumbnailElement(asset, index);
  }
  if (isImage(asset)) {
    return getThumbnailElement(asset.image);
  }
  return undefined;
};

const handleImage = (asset: ProductGalleryAsset, index: number, onVideoClick: () => void) => {
  if (isInteractiveImage(asset)) {
    return getInteractiveImageElement(asset.image);
  }
  if (isVideo(asset) && asset.video && onVideoClick) {
    return getVideoImageElement(asset, index, onVideoClick);
  }
  if (isImage(asset)) {
    return getImageElement(asset.image);
  }
  return undefined;
};

const handleEnlargedImage = (asset: ProductGalleryAsset, autoPlayFlag: number) => {
  if (isInteractiveImage(asset)) {
    return getInteractiveImageElement(asset.enlargedImage);
  }
  if (isVideo(asset) && asset.video) {
    return getVideoElement(asset.video, asset.id, autoPlayFlag);
  }
  if (isImage(asset)) {
    return getEnlargedImageElement(asset.image);
  }
  return undefined;
};

const buildAssets = (
  imageType: 'image' | 'enlarged-image' | 'thumbnail-image',
  assets: ProductGalleryAsset[],
  hasPreviews: boolean,
  currentItem: number,
  setIsOpenModal?: React.Dispatch<React.SetStateAction<boolean>> | undefined,
): ImageComponent[] =>
  assets.reduce((builtAssets, asset, index) => {
    const autoPlayFlag = (hasPreviews && index + 2 === currentItem)
      || (!hasPreviews && index + 1 === currentItem) ? 1 : 0;

    const onVideoClick = () => {
      if (setIsOpenModal) setIsOpenModal(true);
      dispatchDataLayerEntry(
        'gallery_video', 'click', { type: 'play_video_button', contentful_id: asset.id },
      );
    };

    const element: ImageComponent[] | undefined = (() => {
      switch (imageType) {
        case 'thumbnail-image':
          return handleThumbnailImage(asset, index);
        case 'image':
          return handleImage(asset, index, onVideoClick);
        case 'enlarged-image':
          return handleEnlargedImage(asset, autoPlayFlag);
        default:
          return handleImage(asset, index, onVideoClick);
      }
    })();

    if (element) {
      builtAssets.push(...element);
    }

    return builtAssets;
  }, [] as ImageComponent[]);

export const ProductGalleryBuilder = ({
  gallery, title, productKey, documentReferenceUrl = null, scenes = [], previewImage = null,
}: Props) => {
  const {
    assets, images, thumbnails, enlargedImages, sceneTypes,
  } = gallery ?? {};

  const selectionMode = useSelectorMachineContext((state) => state.context.selectionMode);
  const selection = useSelectorMachineContext((state) => state.context.selection);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(1);

  const { isInitialized } = useContext(FeatureManagementContext);
  const { experiment: exp764Experiment } = useExperiment('exp764_clickable_product_tags_in_gallery-v2');
  const showAssets = assets && assets.length > 0;

  const defaultScenes = getPreviews(scenes, sceneTypes);
  const defaultScenesThumbnails = getPreviews(scenes, sceneTypes, 'pd-bg-white');
  const hasDefaultScenes = defaultScenes.length > 0 && defaultScenesThumbnails.length > 0;
  const [hasPreviews, setHasPreviews] = useState(hasDefaultScenes);
  const isRedirectedFromDigitalCatalogue = Boolean(previewImage);

  // loads the YouTube IFrame Player API
  useEffect(() => {
    if (showAssets) {
      const hasVideoAssets = assets.filter((asset) => isVideo(asset));
      if (hasVideoAssets.length > 0) {
        loadYouTubeAPI();
      }
    }
  }, [showAssets]);

  useEffect(() => {
    if (isInitialized && exp764Experiment.variation === 'variant') {
      dispatchDataLayerEntry(
        'product_detail_page', 'view_interactive_image', {
          experiment: exp764Experiment.id,
        },
      );
    }
  }, [isInitialized, exp764Experiment]);

  const { previews, status: previewStatus } = useScenePreviews(productKey, documentReferenceUrl);
  const { data: views, status: viewStatus } = usePreviews({
    variant: 'scene',
    mcpSku: productKey || '',
    mcpAttributes: selection,
    enabled: selectionMode === 'default-values',
  });

  const { processedImages, processedEnlargedImages, processedThumbnails } = useMemo(() =>
    getUpdatedGalleryAssets(images, thumbnails, enlargedImages, previewImage),
  [images, thumbnails, enlargedImages, previewImage]);

  const allOrFilteredAssets = useMemo(() => {
    if (!assets) return [];

    if (isInitialized && exp764Experiment.variation === 'variant') {
      return [...assets];
    }
    return assets.filter((asset) => !isInteractiveImage(asset));
  }, [isInitialized, exp764Experiment.variation, assets]);

  const imageAssets = useMemo(() => {
    if (showAssets) {
      return buildAssets('image', allOrFilteredAssets, hasPreviews, currentItem, setIsOpenModal);
    }
    return getGalleryImages(processedImages);
  }, [showAssets, processedImages, allOrFilteredAssets, hasPreviews, currentItem]);

  const thumbnailAssets = useMemo(() => {
    if (showAssets) {
      return buildAssets('thumbnail-image', allOrFilteredAssets, hasPreviews, currentItem);
    }
    return getThumbnailImages(processedThumbnails);
  }, [showAssets, processedThumbnails, allOrFilteredAssets, hasPreviews, currentItem]);

  const enlargedAssets = useMemo(() => {
    if (showAssets) {
      return buildAssets('enlarged-image', allOrFilteredAssets, hasPreviews, currentItem);
    }
    return getEnlargedImages(processedEnlargedImages);
  }, [showAssets, processedEnlargedImages, allOrFilteredAssets, hasPreviews, currentItem]);

  const [imageComponents, setImageComponents] = useState<ImageComponent[]>([]);
  const [enlargedImageComponents, setEnlargedImageComponents] = useState<ImageComponent[]>([]);
  const [thumbnailComponents, setThumbnailComponents] = useState<ImageComponent[]>([]);

  useEffect(() => {
    /**
     * Making preview image from cloudlab the first image of the gallery as per requirement
     * https://drukwerkdeal.atlassian.net/browse/IMPRESSTO-11
     */
    const thumbnailsOrdered = isRedirectedFromDigitalCatalogue
      ? thumbnailAssets.concat(defaultScenesThumbnails)
      : defaultScenesThumbnails.concat(thumbnailAssets);

    const imagesOrdered = isRedirectedFromDigitalCatalogue
      ? imageAssets.concat(defaultScenes)
      : defaultScenes.concat(imageAssets);

    const enlargedImagesOrdered = isRedirectedFromDigitalCatalogue
      ? enlargedAssets.concat(defaultScenes)
      : defaultScenes.concat(enlargedAssets);

    setThumbnailComponents(thumbnailsOrdered);
    setImageComponents(imagesOrdered);
    setEnlargedImageComponents(enlargedImagesOrdered);
  }, [allOrFilteredAssets, isOpenModal]);

  useEffect(() => {
    if (previewStatus === 'loaded' && previews.length > 0) {
      /**
       * Making preview image from cloudlab the first image of the gallery as per requirement
       * https://drukwerkdeal.atlassian.net/browse/IMPRESSTO-11
       */
      const thumbnailsOrdered = isRedirectedFromDigitalCatalogue
        ? [...thumbnailAssets, ...getPreviews(previews, sceneTypes, 'pd-bg-white')]
        : [...getPreviews(previews, sceneTypes, 'pd-bg-white'), ...thumbnailAssets];

      const imagesOrdered = isRedirectedFromDigitalCatalogue
        ? [...imageAssets, ...getPreviews(previews, sceneTypes)]
        : [...getPreviews(previews, sceneTypes), ...imageAssets];

      const enlargedImagesOrdered = isRedirectedFromDigitalCatalogue
        ? [...enlargedAssets, ...getPreviews(previews, sceneTypes)]
        : [...getPreviews(previews, sceneTypes), ...enlargedAssets];

      setHasPreviews(true);
      setThumbnailComponents(thumbnailsOrdered);
      setImageComponents(imagesOrdered);
      setEnlargedImageComponents(enlargedImagesOrdered);
    }
  }, [allOrFilteredAssets, isOpenModal, JSON.stringify(previews), previewStatus]);

  useEffect(() => {
    if (viewStatus === 'success' && views.length > 0) {
      const canvasPreviews = getCanvasPreviews(documentReferenceUrl, views);
      const thumbnailsOrdered = canvasPreviews.concat(thumbnailAssets);
      const imagesOrdered = canvasPreviews.concat(imageAssets);
      const enlargedImagesOrdered = canvasPreviews.concat(enlargedAssets);

      setHasPreviews(true);
      setThumbnailComponents(thumbnailsOrdered);
      setImageComponents(imagesOrdered);
      setEnlargedImageComponents(enlargedImagesOrdered);
    }
  }, [
    allOrFilteredAssets,
    documentReferenceUrl,
    enlargedAssets,
    imageAssets,
    isOpenModal,
    selectionMode,
    thumbnailAssets,
    viewStatus,
    views,
  ]);

  const controls = useMemo(() => ({
    isOpenModal, setIsOpenModal, currentItem, setCurrentItem,
  }), [isOpenModal, currentItem]);

  return (
    <ProductGallery
      imageComponents={imageComponents}
      enlargedImageComponents={enlargedImageComponents}
      thumbnailComponents={thumbnailComponents}
      modalTitle={title}
      pageName="product_detail_page"
      controls={controls}
    />
  );
};
